<template>
  <f7-page class="home-page">
    <template #fixed>
      <MainHeaderComponent type="back" />
    </template>

    <div class="bg-gray title-container">
      <div class="title">
        <font-awesome-icon :icon="['fad', 'cog']" fixed-width />

        <div class="text">
          <h1>{{ $t.getTranslation('LBL_CATEGORY_SAVE') }}</h1>
        </div>
      </div>

      <div class="button-container"></div>
    </div>

    <section class="form-container">
      <div class="container">
        <f7-list no-hairlines>
          <f7-list-item divider>{{ $t.getTranslation('LBL_CATEGORY_DETAILS') }}</f7-list-item>
          <f7-list-input
            v-model:value="formData.CategoryCode"
            name="CategoryCode"
            :required="validationRules?.CategoryCode?.required"
            :minlength="validationRules?.CategoryCode?.minimumLength"
            :maxlength="validationRules?.CategoryCode?.maximumLength"
            :label="$t.getTranslation('LBL_CATEGORY_CODE')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            :disabled="formData.CategoryId"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.CategoryName.EN"
            name="CategoryName"
            :required="validationRules?.CategoryName?.required"
            :minlength="validationRules?.CategoryName?.minimumLength"
            :maxlength="validationRules?.CategoryName?.maximumLength"
            :label="$t.getTranslation('LBL_CATEGORY_NAME_EN')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.CategoryName.CHS"
            name="CategoryName"
            :required="validationRules?.CategoryName?.required"
            :minlength="validationRules?.CategoryName?.minimumLength"
            :maxlength="validationRules?.CategoryName?.maximumLength"
            :label="$t.getTranslation('LBL_CATEGORY_NAME_CHS')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="text"
            validate
          >
          </f7-list-input>

          <f7-list-input
            v-if="formData.ShowTaxRate"
            v-model:value="formData.TaxRate"
            name="TaxRate"
            :required="validationRules?.TaxRate?.required"
            :minlength="validationRules?.TaxRate?.minimumLength"
            :maxlength="validationRules?.TaxRate?.maximumLength"
            :label="$t.getTranslation('LBL_TAX_RATE')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_REQUIRED')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="number"
          >
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.StatusCode"
            name="StatusCode"
            :required="validationRules?.StatusCode?.required"
            :minlength="validationRules?.StatusCode?.minimumLength"
            :maxlength="validationRules?.StatusCode?.maximumLength"
            :label="$t.getTranslation('LBL_STATUS')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_OPTIONAL')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="select"
            validate
          >
            <option v-for="status in statusList" :key="'st_' + status.Code" :value="status.Code">{{ status.Name }}</option>
          </f7-list-input>

          <f7-list-input
            v-model:value="formData.IsFeatured"
            name="Status"
            :required="validationRules?.IsFeatured?.required"
            :minlength="validationRules?.IsFeatured?.minimumLength"
            :maxlength="validationRules?.IsFeatured?.maximumLength"
            :label="$t.getTranslation('LBL_FEATURED_STATUS')"
            :placeholder="$t.getTranslation('LBL_TYPE_HERE')"
            :info="$t.getTranslation('LBL_INFO_FIELD_OPTIONAL')"
            :error-message="$t.getLanguage() != 'EN' ? $t.getTranslation('LBL_ERROR_FIELD') : ''"
            floating-label
            outline
            type="select"
            validate
          >
            <option v-for="status in featuredStatusList" :key="'st_' + status.Code" :value="status.Code">{{ status.Name }}</option>
          </f7-list-input>

          <div v-if="formData.CategoryId">
            <f7-list-item>
              <ViewImageListComponent :title="$t.getTranslation('LBL_UPLOADED_IMAGES')" :data="uploadedImages" />
            </f7-list-item>

            <f7-segmented raised tag="p">
              <f7-button @click="upload('CATEGORY_IMAGE')">{{ $t.getTranslation('LBL_UPLOAD_IMAGE') }}</f7-button>
            </f7-segmented>
          </div>

          <f7-button large fill raised preloader @click="save">
            {{ $t.getTranslation('LBL_SAVE') }}
          </f7-button>

          <f7-button large fill raised preloader @click="back">
            {{ $t.getTranslation('LBL_BACK') }}
          </f7-button>
        </f7-list>
      </div>
    </section>

    <MainFooterComponent />
  </f7-page>
</template>

<script>
import { defineComponent, ref, computed, reactive, onMounted, inject } from 'vue'

import { useStore } from '@/store'

import { $HTTP } from '@/utils/axios'
import { helpers } from '@/utils/helpers.js'

import { validations } from '@/utils/validations'
import validationRulesConfig from '@/validationRules'

import MainHeaderComponent from '@/components/MainHeaderComponent.vue'
import MainFooterComponent from '@/components/MainFooterComponent.vue'
import ViewImageListComponent from '@/components/ViewImageListComponent.vue'

export default defineComponent({
  name: 'CategorySavePage',
  components: {
    MainHeaderComponent,
    MainFooterComponent,
    ViewImageListComponent
  },
  props: { f7router: Object, f7route: Object },
  setup(props) {
    const $t = inject('$translation')
    const store = useStore()
    const isUpdate = ref(false)
    const isButtonProcessing = ref(false)
    const statusList = computed(() => store.getters['config/getData']?.statusList)
    const currentLanguageCode = computed(() => store.getters['translation/getLanguage'])
    const uploadedImages = ref([])
    const categoryId = ref(null)
    const formData = reactive({
      CategoryCode: '',
      CategoryName: '',
      ShowTaxRate: true
    })

    const hideTaxRateCategory = ref(['BUNDLE_SALE', 'GROUP_BUY', 'FLASH_SALE'])

    const featuredStatusList = ref([
      {
        Code: 1,
        Name: $t.getTranslation('LBL_ACTIVE')
      },
      {
        Code: 0,
        Name: $t.getTranslation('LBL_DISABLED')
      }
    ])

    // uploadedImages.value.push({fields: "Identification", name: "Identification", link: helpers.getImage('http://localhost:3003/img/avatar_male.1e0b4bfd.svg', null, null, true)});

    let validationRules = {}
    for (let keys in formData) {
      if (validationRulesConfig && validationRulesConfig[keys]) {
        validationRules[keys] = validationRulesConfig[keys]
      }
    }

    onMounted(async () => {
      if (props?.f7route?.query && props?.f7route?.query?.CategoryId) {
        isUpdate.value = true
        categoryId.value = props?.f7route?.query?.CategoryId
        await getData()
        if (hideTaxRateCategory.value.includes(formData.CategoryCode)) {
          formData.ShowTaxRate = false
        }
      }
    })

    const getData = async () => {
      try {
        helpers.showLoader()
        let res = await $HTTP.get(`/admin/category/view?CategoryId=${categoryId.value}&LanguageCode=${currentLanguageCode.value || 'EN'}`)
        if (res?.status === 200 && res?.data?.data) {
          helpers.hideLoader()
          for (var key in res?.data?.data) {
            formData[key] = res?.data?.data[key]
          }

          if (uploadedImages.value.length == 0) {
            uploadedImages.value.push({ fields: res.data.data.FieldName, name: $t.getTranslation('LBL_CATEGORY_IMAGE'), link: helpers.getImage(res.data.data.FileName, 'CATEGORY') })
          }

          helpers.hideLoader()
          return
        }
        throw new Error($t.getTranslation('ERR_SERVER_NOT_RESPONDING'))
      } catch (err) {
        helpers.hideLoader()
        helpers.catchError(err, true)
      }
    }

    const validate = (showNotifications) => {
      const isValid = validations.validate({
        validateData: formData,
        validationRules: validationRules,
        showNotifications: showNotifications
      })

      return isValid
    }

    const isDisableSaveButton = computed(() => {
      let isValid = validate(false)

      return !isValid
    })

    const upload = (type) => {
      props.f7router.navigate({ name: 'categoryUpload', params: { id: categoryId.value }, query: { type: type } })
    }

    const back = () => {
      props.f7router.navigate({ name: 'categoryList', params: {}, query: {} })
    }

    const save = async () => {
      try {
        helpers.showLoader()
        let res = await $HTTP.post(`/admin/category/save`, formData)
        if (res?.status === 200 && res?.data?.data) {
          helpers.createNotification({ type: 'success', title: $t.getTranslation('LBL_SUCCESS'), message: $t.getTranslation('LBL_INFO_SUCCESSFULLY_SAVE_CATEGORY_INFO') })
          helpers.hideLoader()
          if (props?.f7route?.query?.isFeatured) {
            //redirect back to category featured position page(categoryPositionList)..
            props.f7router.navigate({ name: 'categoryPositionList', params: {}, query: {} })
            return
          }
          return
        }
        throw new Error($t.getTranslation('ERR_SERVER_NOT_RESPONDING'))
      } catch (err) {
        helpers.hideLoader()
        helpers.catchError(err, true)
      }
    }

    return { isUpdate, formData, validationRules, isButtonProcessing, isDisableSaveButton, uploadedImages, upload, back, save, statusList, featuredStatusList }
  }
})
</script>
